import { ApiError } from '@/api/client';
import { getSessionUser, sessionIdentity } from '@/auth/session';
import { FieldConflictError, fieldRequest } from './api';

/**
 * The offline outbox.
 *
 * A technician in a crawl space is not "offline" in the sense a web application usually means. They
 * have a whole visit to run — arrive, tick six things, photograph a serial plate, write what they
 * found, take a signature, finish — and no connection for any of it. The queue is therefore not a
 * retry wrapper around a failed request; it is where the work is recorded in the first place, and
 * the network is a thing that happens to it later.
 *
 * Four properties, and each is the answer to a way this goes wrong in the field:
 *
 * 1. **Order is preserved.** A status change queued before a checklist tick is applied before it.
 *    Applying them in whatever order the network happens to allow would let a visit be finished
 *    before it was started, and the server would refuse the second one for reasons nobody could
 *    explain. So a transport failure stops the drain rather than skipping past it.
 * 2. **Re-delivery is safe.** Every entry carries an operation key chosen when it is queued, not
 *    when it is sent, so a key survives a reload, a battery death and a second drain of an entry
 *    whose reply was lost. The server applies a key once; a replay returns the first answer.
 * 3. **A refusal is not a retry.** A conflict or a validation failure will never succeed by being
 *    sent again, so it is marked and taken out of the running order instead of blocking every
 *    later entry behind it forever. A transport failure is the opposite and is retried, with a
 *    backoff, in place.
 * 4. **Nothing is silently dropped.** An entry leaves the queue when the server has accepted it or
 *    when a human has been shown why it cannot be accepted, and never otherwise.
 */

/** Which field operation an entry carries. Mirrors the server's ledger kinds. */
export type OutboxKind = 'TRANSITION' | 'CHECKLIST' | 'NOTE' | 'PHOTO' | 'SIGNATURE' | 'HANDOFF';

/** Where an entry has got to. */
export type OutboxStatus = 'pending' | 'failed' | 'conflict';

/** One queued mutation. */
export interface OutboxEntry {
  /** The operation key, chosen at queue time; also this entry's identity. */
  readonly key: string;
  /** Which operation it is. */
  readonly kind: OutboxKind;
  /** The visit it belongs to. */
  readonly jobId: string;
  /** The path below /api/field. */
  readonly path: string;
  /** The HTTP method. */
  readonly method: 'POST' | 'PUT';
  /** The request body, already carrying `operationKey` and `recordedAt`. */
  readonly body: Record<string, unknown>;
  /** What to call this in the queue list. */
  readonly label: string;
  /** When the technician did the thing. */
  readonly queuedAt: string;
  /** How many times delivery has been attempted. */
  readonly attempts: number;
  /** Where it has got to. */
  readonly status: OutboxStatus;
  /** Why it is not going anywhere, when it is not. */
  readonly error?: string;
  /** The earliest moment another attempt is worth making. */
  readonly nextAttemptAt: number;
  /**
   * Who recorded it: the session identity (user and workspace) at enqueue time. A shared tablet
   * holds several technicians' queues, and an entry is only ever shown to, and sent with the token
   * of, the principal who recorded it. Absent on entries written before this was stamped, which
   * therefore belong to nobody who can sign in.
   */
  readonly owner?: string;
}

/** Somewhere to keep the queue across reloads. */
export interface OutboxStore {
  /** Every entry, in the order it was queued. */
  all: () => Promise<OutboxEntry[]>;
  /** Writes one entry, replacing any entry with the same key. */
  put: (entry: OutboxEntry) => Promise<void>;
  /** Forgets one entry. */
  remove: (key: string) => Promise<void>;
}

/** The database and store field mode keeps its queue in. */
export const OUTBOX_DB = 'fieldops-outbox';

/** The object store inside it. */
export const OUTBOX_STORE = 'operations';

/**
 * A queue that lives only as long as the tab.
 *
 * The fallback when IndexedDB is unavailable — a private window in some browsers, a hardened
 * enterprise profile, and jsdom. Degrading to this is not the same as working: the queue survives
 * a network outage but not a reload, and the screen says so.
 *
 * @returns an in-memory store
 */
export function memoryStore(): OutboxStore {
  const rows = new Map<string, OutboxEntry>();
  return {
    all: () => Promise.resolve([...rows.values()]),
    put: (entry) => {
      rows.set(entry.key, entry);
      return Promise.resolve();
    },
    remove: (key) => {
      rows.delete(key);
      return Promise.resolve();
    },
  };
}

function transactionRequest<T>(
  transaction: IDBTransaction,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    let result: T;
    let settled = false;
    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      reject(error instanceof Error ? error : new Error('The offline queue transaction failed.'));
    };
    transaction.oncomplete = () => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    transaction.onerror = () => {
      fail(transaction.error ?? new Error('The offline queue transaction failed.'));
    };
    transaction.onabort = () => {
      fail(transaction.error ?? new Error('The offline queue transaction was aborted.'));
    };
    let req: IDBRequest<T>;
    try {
      req = run(transaction.objectStore(OUTBOX_STORE));
    } catch (error) {
      fail(error);
      return;
    }
    req.onsuccess = () => {
      result = req.result;
    };
    req.onerror = () => {
      fail(req.error ?? new Error('The offline queue request failed.'));
    };
  });
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const opening = indexedDB.open(OUTBOX_DB, 1);
    opening.onupgradeneeded = () => {
      const db = opening.result;
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
        db.createObjectStore(OUTBOX_STORE, { keyPath: 'key' });
      }
    };
    opening.onsuccess = () => {
      resolve(opening.result);
    };
    opening.onerror = () => {
      reject(opening.error ?? new Error('The offline queue could not be opened.'));
    };
  });
}

/**
 * A queue that survives the tab being closed.
 *
 * IndexedDB rather than localStorage for one reason that matters here: a queued photograph is a
 * megabyte of base64, localStorage is a synchronous five-megabyte cupboard shared with everything
 * else, and a technician who photographs six serial plates in a basement would fill it and lose
 * the morning.
 *
 * @returns a persistent store, or an in-memory one where IndexedDB is unavailable
 */
export function persistentStore(): OutboxStore {
  if (typeof indexedDB === 'undefined') {
    return memoryStore();
  }
  const withStore = async <T>(
    mode: IDBTransactionMode,
    // Not named `use`: ESLint reads a bare `use(...)` call as React's hook and refuses it inside a
    // try block, which this genuinely needs.
    run: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> => {
    const db = await open();
    try {
      return await transactionRequest(db.transaction(OUTBOX_STORE, mode), run);
    } finally {
      db.close();
    }
  };

  return {
    all: async () => {
      const rows = await withStore<unknown[]>('readonly', (store) => store.getAll());
      return (rows as OutboxEntry[]).slice().sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
    },
    put: async (entry) => {
      await withStore('readwrite', (store) => store.put(entry));
    },
    remove: async (key) => {
      await withStore('readwrite', (store) => store.delete(key));
    },
  };
}

/** What one drain of the queue did. */
export interface FlushResult {
  /** How many entries the server accepted. */
  readonly sent: number;
  /** Entries the server refused because the office had already moved on. */
  readonly conflicts: OutboxEntry[];
  /** Entries the server refused for a reason retrying cannot fix. */
  readonly rejected: OutboxEntry[];
  /** True when the drain stopped early because the network is not there. */
  readonly stalled: boolean;
  /**
   * True when the drain stopped because the session cannot send this work: the server answered
   * 401, or the tab is now signed in as somebody other than the entry's owner. Nothing was marked
   * refused; the work waits for its owner to sign in again.
   */
  readonly unauthenticated: boolean;
}

/** How the queue may be adjusted from outside. */
export interface OutboxOptions {
  /** Where to keep the queue. */
  readonly store?: OutboxStore;
  /** How to send one entry. Injected so tests can drive it without a network. */
  readonly send?: (entry: OutboxEntry) => Promise<unknown>;
  /** The clock, injected for the same reason. */
  readonly now?: () => number;
  /** Whose queue this is to begin with; see {@link Outbox.setOwner}. */
  readonly owner?: string | null;
  /**
   * Who the live session belongs to right now, read before every send. Defaults to the signed-in
   * user; injected for tests.
   */
  readonly sessionIdentity?: () => string | null;
  /** Where keys awaiting deletion from the store survive a reload; defaults to localStorage. */
  readonly removals?: KeyLedger;
}

/** A small durable list of keys. */
export interface KeyLedger {
  read: () => Set<string>;
  write: (keys: Set<string>) => void;
}

const REMOVALS_KEY = 'fieldops-outbox-removed';

/**
 * Keys this device removed from the queue but could not yet delete from IndexedDB.
 *
 * Kept outside IndexedDB on purpose: it is needed exactly when IndexedDB is failing, and without it
 * an entry the server had already accepted — or a refusal a human had dismissed — would come back
 * from the database after a reload and be sent again.
 */
function localStorageLedger(): KeyLedger {
  return {
    read: () => {
      try {
        const parsed: unknown = JSON.parse(globalThis.localStorage.getItem(REMOVALS_KEY) ?? '[]');
        return new Set(
          Array.isArray(parsed) ? parsed.filter((key) => typeof key === 'string') : [],
        );
      } catch {
        return new Set();
      }
    },
    write: (keys) => {
      try {
        if (keys.size === 0) {
          globalThis.localStorage.removeItem(REMOVALS_KEY);
        } else {
          globalThis.localStorage.setItem(REMOVALS_KEY, JSON.stringify([...keys]));
        }
      } catch {
        // Best effort: the in-memory set still covers this tab.
      }
    },
  };
}

const STORAGE_WARNING =
  'Durable offline storage is unavailable. New work will remain only while this tab is open; keep it open until it sends.';

const SIGN_IN_AGAIN = 'Waiting for you to sign in again; nothing has been lost.';

function byQueuedAt(a: OutboxEntry, b: OutboxEntry): number {
  return a.queuedAt.localeCompare(b.queuedAt);
}

/** First retry delay, doubling to {@link MAX_BACKOFF_MS}. */
export const BASE_BACKOFF_MS = 2_000;

/** Longest a retry ever waits. Beyond this, waiting is not the problem. */
export const MAX_BACKOFF_MS = 60_000;

function backoffFor(attempts: number): number {
  return Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** Math.max(0, attempts - 1));
}

/**
 * Whether a failure is worth trying again.
 *
 * The distinction is the queue's most important judgement. A 4xx is the server saying no, and
 * saying it again will not change the answer — so retrying forever would block every later entry
 * behind an entry that can never leave. Anything else, including no response at all, is the
 * network, and the network comes back.
 *
 * @param error whatever the send threw
 * @returns true when another attempt may succeed
 */
export function isTransportFailure(error: unknown): boolean {
  if (error instanceof FieldConflictError) {
    return false;
  }
  if (error instanceof ApiError) {
    return error.status >= 500 || error.status === 408 || error.status === 429;
  }
  return true;
}

/**
 * Whether a failure means the session, not the work, is the problem.
 *
 * A 401 is not the server refusing the entry; it is the server not knowing who is asking — an
 * expired token whose refresh could not reach the server, or a session that ended. Marking the
 * whole queue refused for it would leave a technician with a morning of work and only a dismiss
 * button. The entry waits, untouched, for its owner to sign in again.
 *
 * @param error whatever the send threw
 * @returns true when the drain should stop and wait for a session
 */
export function isAuthenticationFailure(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/**
 * The queue itself.
 *
 * Holds its owner's entries in memory for the screen to render and mirrors every change into the
 * store, so a reload finds the queue where it left it. The store is shared by everybody who signs in
 * on the device; each principal only ever sees, and sends, their own entries.
 */
export class Outbox {
  private readonly store: OutboxStore;
  private readonly send: (entry: OutboxEntry) => Promise<unknown>;
  private readonly now: () => number;
  private readonly liveIdentity: () => string | null;
  private readonly removalLedger: KeyLedger;
  private readonly listeners = new Set<(entries: OutboxEntry[]) => void>();
  /** Every entry this tab has written, for every owner, in its latest state. */
  private readonly mirror = new Map<string, OutboxEntry>();
  /** Keys whose latest state the store may not hold, because writing it failed. */
  private readonly unsaved = new Set<string>();
  /** Keys removed here whose stored copy may still exist, because deleting it failed. */
  private readonly removed: Set<string>;
  private owner: string | null;
  private rows: OutboxEntry[] = [];
  private draining: Promise<FlushResult> | null = null;
  private reconciling = false;
  private storageFailure: string | null = null;

  constructor(options: OutboxOptions = {}) {
    this.store = options.store ?? persistentStore();
    this.send =
      options.send ?? ((entry) => fieldRequest<unknown>(entry.path, entry.method, entry.body));
    this.now = options.now ?? (() => Date.now());
    this.owner = options.owner ?? null;
    this.liveIdentity = options.sessionIdentity ?? (() => sessionIdentity(getSessionUser()));
    this.removalLedger = options.removals ?? localStorageLedger();
    this.removed = this.removalLedger.read();
  }

  /**
   * Says whose queue this is.
   *
   * Changing owner empties the visible queue at once, so the next person to sign in on a shared
   * device never sees the previous one's work while {@link hydrate} reads their own.
   *
   * @param owner the session identity, or null when nobody is signed in
   */
  setOwner(owner: string | null): void {
    if (owner === this.owner) {
      return;
    }
    this.owner = owner;
    this.rows = [];
    this.publish();
  }

  /**
   * Loads whatever this owner's last session left behind.
   *
   * Other owners' entries are left exactly where they are.
   *
   * @returns the entries now held
   */
  async hydrate(): Promise<OutboxEntry[]> {
    const owner = this.owner;
    let stored: OutboxEntry[] = [];
    try {
      // Caught up first, so a key deleted here is not read back as though it were still waiting.
      await this.reconcile();
      stored = await this.store.all();
    } catch {
      this.storageFailure = STORAGE_WARNING;
    }
    if (owner !== this.owner) {
      // Somebody else signed in while this was reading; their own hydrate answers for them.
      return this.rows;
    }
    const merged = new Map<string, OutboxEntry>();
    for (const entry of stored) {
      if (!this.removed.has(entry.key)) {
        merged.set(entry.key, entry);
      }
    }
    for (const entry of this.mirror.values()) {
      merged.set(entry.key, entry);
    }
    this.rows = [...merged.values()]
      .filter((entry) => (entry.owner ?? null) === owner)
      .sort(byQueuedAt);
    this.publish();
    return this.rows;
  }

  /** @returns the entries now held, in queue order */
  entries(): OutboxEntry[] {
    return this.rows;
  }

  /**
   * Explains when the queue could not reach IndexedDB.
   *
   * The queue still accepts work in this state, but the work can only survive while this tab is
   * open. Screens use this to make the degradation explicit instead of claiming that a change was
   * safely saved for a later reload. It clears once the store accepts writes again and has caught up.
   *
   * @returns a user-facing warning, or null while durable storage is healthy
   */
  storageWarning(): string | null {
    return this.storageFailure;
  }

  /** @returns how many entries are still waiting to be sent */
  pending(): number {
    return this.rows.filter((entry) => entry.status === 'pending').length;
  }

  /**
   * Watches the queue.
   *
   * @param listener called with every change
   * @returns a function that stops watching
   */
  subscribe(listener: (entries: OutboxEntry[]) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Puts one mutation in the queue, stamped with the current owner.
   *
   * @param entry the mutation, minus the bookkeeping this fills in
   * @returns the queued entry
   */
  async enqueue(
    entry: Omit<OutboxEntry, 'attempts' | 'status' | 'nextAttemptAt' | 'owner'>,
  ): Promise<OutboxEntry> {
    const queued: OutboxEntry = {
      ...entry,
      attempts: 0,
      status: 'pending',
      nextAttemptAt: 0,
      ...(this.owner === null ? {} : { owner: this.owner }),
    };
    // Saved before publishing the new row. If IndexedDB is full or unavailable, the save keeps the
    // entry in memory and exposes a warning, so the screen never silently claims that work will
    // survive a reload.
    await this.save(queued);
    this.rows = [...this.rows.filter((row) => row.key !== queued.key), queued];
    this.publish();
    return queued;
  }

  /**
   * Puts a refused entry back in the running order.
   *
   * For a refusal that was about the moment rather than the work — a permission restored, a
   * payload the office has since made valid. The operation key is kept: the server records a key
   * only when it applies the work, so a refused key is still free.
   *
   * @param key the entry's operation key
   */
  async retry(key: string): Promise<void> {
    const found = this.rows.find((row) => row.key === key);
    if (found?.status !== 'failed') {
      return;
    }
    const { error: _error, ...rest } = found;
    await this.replace({ ...rest, status: 'pending', nextAttemptAt: 0 });
  }

  /**
   * Forgets an entry a human has read and dismissed.
   *
   * Only ever called for an entry that is already `conflict` or `failed`: a pending entry is work
   * nobody has seen the outcome of, and dropping it would be losing a technician's morning.
   *
   * @param key the entry's operation key
   */
  async dismiss(key: string): Promise<void> {
    const found = this.rows.find((row) => row.key === key);
    if (found === undefined || found.status === 'pending') {
      return;
    }
    await this.forget(key);
  }

  /**
   * Drains the queue, in order, until it empties or the network stops it.
   *
   * Concurrent calls share one drain. Reconnecting fires `online`, a screen mounts, and a timer
   * ticks, all within a second of one another; three drains racing would send the same entry three
   * times, and while the server would tolerate that — that is what the keys are for — the queue
   * would be reasoning about its own duplicate sends.
   *
   * @returns what the drain did
   */
  flush(): Promise<FlushResult> {
    this.draining ??= this.drain().finally(() => {
      this.draining = null;
    });
    return this.draining;
  }

  private async drain(): Promise<FlushResult> {
    const conflicts: OutboxEntry[] = [];
    const rejected: OutboxEntry[] = [];
    let sent = 0;
    let stalled = false;
    let unauthenticated = false;

    // A snapshot, not a live read: entries queued while this drain is running belong to the next
    // one, and walking a list that grows underneath would let a photograph taken mid-drain jump
    // ahead of the note written before it.
    for (const entry of [...this.rows]) {
      if (entry.status !== 'pending') {
        continue;
      }
      if (entry.nextAttemptAt > this.now()) {
        // Still in its backoff. Later entries wait with it, because order is the point.
        stalled = true;
        break;
      }
      // Checked before every send, not once per drain: another tab can sign in as somebody else
      // mid-drain, and the next request would carry their token into their workspace.
      const live = this.liveIdentity();
      if (live !== null && live !== (entry.owner ?? null)) {
        stalled = true;
        unauthenticated = true;
        break;
      }
      try {
        await this.send(entry);
        sent += 1;
        await this.forget(entry.key);
      } catch (error) {
        if (isAuthenticationFailure(error)) {
          await this.replace({ ...entry, error: SIGN_IN_AGAIN });
          stalled = true;
          unauthenticated = true;
          break;
        }
        if (isTransportFailure(error)) {
          const attempts = entry.attempts + 1;
          await this.replace({
            ...entry,
            attempts,
            nextAttemptAt: this.now() + backoffFor(attempts),
            error: error instanceof Error ? error.message : 'No connection.',
          });
          stalled = true;
          break;
        }
        const marked: OutboxEntry = {
          ...entry,
          attempts: entry.attempts + 1,
          status: error instanceof FieldConflictError ? 'conflict' : 'failed',
          error:
            error instanceof FieldConflictError
              ? error.current.detail
              : error instanceof Error
                ? error.message
                : 'The server refused this.',
        };
        await this.replace(marked);
        (marked.status === 'conflict' ? conflicts : rejected).push(marked);
      }
    }

    return { sent, conflicts, rejected, stalled, unauthenticated };
  }

  private async forget(key: string): Promise<void> {
    this.rows = this.rows.filter((row) => row.key !== key);
    await this.erase(key);
    this.publish();
  }

  private async replace(entry: OutboxEntry): Promise<void> {
    this.rows = this.rows.map((row) => (row.key === entry.key ? entry : row));
    await this.save(entry);
    this.publish();
  }

  // Every write tries IndexedDB afresh. One failed transaction — a quota blip, a locked database —
  // must not leave the rest of the session writing to memory while the stored copies go stale.
  private async save(entry: OutboxEntry): Promise<void> {
    this.mirror.set(entry.key, entry);
    try {
      await this.store.put(entry);
      this.unsaved.delete(entry.key);
      await this.reconcile();
    } catch {
      this.unsaved.add(entry.key);
      this.storageFailure = STORAGE_WARNING;
    }
  }

  private async erase(key: string): Promise<void> {
    this.mirror.delete(key);
    this.unsaved.delete(key);
    try {
      await this.store.remove(key);
      if (this.removed.delete(key)) {
        this.removalLedger.write(this.removed);
      }
      await this.reconcile();
    } catch {
      this.removed.add(key);
      this.removalLedger.write(this.removed);
      this.storageFailure = STORAGE_WARNING;
    }
  }

  /** Catches the store up on whatever earlier writes and deletes failed to reach it. */
  private async reconcile(): Promise<void> {
    if (this.removed.size === 0 && this.unsaved.size === 0) {
      this.storageFailure = null;
      return;
    }
    if (this.reconciling) {
      return;
    }
    this.reconciling = true;
    try {
      for (const key of [...this.removed]) {
        await this.store.remove(key);
        this.removed.delete(key);
      }
      for (const key of [...this.unsaved]) {
        const entry = this.mirror.get(key);
        if (entry !== undefined) {
          await this.store.put(entry);
        }
        this.unsaved.delete(key);
      }
      this.storageFailure = null;
    } catch (error) {
      this.storageFailure = STORAGE_WARNING;
      throw error;
    } finally {
      this.removalLedger.write(this.removed);
      this.reconciling = false;
    }
  }

  private publish(): void {
    for (const listener of this.listeners) {
      listener(this.rows);
    }
  }
}

/**
 * Counts the work a principal still has waiting on this device.
 *
 * For the sign-out control, which lives outside field mode and has no queue of its own mounted:
 * signing out with work waiting leaves it on the device, unsendable until the same person signs in
 * here again, and they should be told before rather than after.
 *
 * @param identity the session identity whose work to count
 * @param store where the queue is kept
 * @returns how many entries are still waiting to be sent
 */
export async function countQueuedWork(
  identity: string | null,
  store: OutboxStore = persistentStore(),
): Promise<number> {
  if (identity === null) {
    return 0;
  }
  const removed = localStorageLedger().read();
  const rows = await store.all().catch((): OutboxEntry[] => []);
  return rows.filter(
    (entry) => entry.owner === identity && entry.status === 'pending' && !removed.has(entry.key),
  ).length;
}

/**
 * Mints an operation key.
 *
 * Chosen when the work is queued, never when it is sent: a key generated at send time would be new
 * on every retry, which is precisely the duplicate the server's ledger exists to refuse.
 *
 * @param kind which operation it is
 * @returns a key unique to this device and moment
 */
export function operationKey(kind: OutboxKind): string {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
  return `${kind.toLowerCase()}-${random}`.slice(0, 80);
}
